import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { CloseBlocker } from '@alafec/shared';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { JournalView } from '../components/accounting/journal-view';
import { DateDisplay } from '../components/common/date-display';
import {
  useAssets,
  useEntries,
  useFecExports,
  useFiscalPeriods,
} from '../lib/api/hooks/use-queries';
import {
  useClosePeriod,
  useConfirmBankSources,
  useConfirmHeld522Assets,
  useConfirmSpamReview,
  useSetManualClosingPrice,
  useSetManualSpotPrice,
} from '../lib/api/hooks/use-mutations';
import type { ClosePeriodResultDto } from '../lib/api/schemas';
import { periodStatusLabel } from '../lib/periods';
import { toast } from '../components/ui/use-toast';
import { useFecExport } from '../components/periods/use-fec-export';
import { FecExportHistory } from '../components/periods/fec-export-history';
import { ClosePreparationCard } from '../components/periods/close-preparation-card';
import { Held522DecisionDialog } from '../components/periods/held-522-decision-dialog';
import { BankSourceDecisionDialog } from '../components/periods/bank-source-decision-dialog';
import { useClosePreparation } from '../components/periods/use-close-preparation';
import { ManualSpotPriceDialog } from '../components/prices/manual-spot-price-dialog';
import { ManualClosingPriceDialog } from '../components/prices/manual-closing-price-dialog';
import { SpamReviewDialog } from '../components/periods/spam-review-dialog';
import {
  closingPricesMissing,
  frenchErrorMessage,
  isCloseBlockedError,
} from '../lib/api/error-messages';

interface ManualSpotTarget {
  readonly assetId: number;
  readonly symbol: string;
  readonly timestamp: number;
}

interface ManualClosingTarget {
  readonly assetId: number;
  readonly symbol: string;
  readonly date: string;
}

interface ClosingPriceFlow {
  readonly missing: readonly { readonly assetId: number; readonly date: string }[];
  readonly index: number;
}

function showCloseSuccess(result: ClosePeriodResultDto): void {
  if (result.warnings.length > 0) {
    toast({
      title: 'Période clôturée — à vérifier',
      description: result.warnings.join(' '),
      variant: 'destructive',
    });
    return;
  }
  toast({
    title: 'Période clôturée',
    description: `${result.cryEntriesCreated} écritures crypto, ${result.valuationEntriesCreated} écritures de valorisation`,
  });
}

function PeriodDetail(): JSX.Element {
  const { periodId } = Route.useParams();
  const id = Number(periodId);
  const periods = useFiscalPeriods();
  const entries = useEntries(id);
  const exports = useFecExports(id);
  const assets = useAssets();
  const preparation = useClosePreparation(id);
  const [heldDialogOpen, setHeldDialogOpen] = useState(false);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [manualSpotTarget, setManualSpotTarget] = useState<ManualSpotTarget | null>(null);
  const [manualClosingTarget, setManualClosingTarget] = useState<ManualClosingTarget | null>(null);
  const [closingPriceFlow, setClosingPriceFlow] = useState<ClosingPriceFlow | null>(null);
  const [spamRawTxId, setSpamRawTxId] = useState<number | null>(null);
  const close = useClosePeriod((error) => {
    const missing = closingPricesMissing(error);
    if (missing !== null) {
      setClosingPriceFlow({ missing, index: 0 });
      return;
    }
    if (isCloseBlockedError(error)) {
      toast({
        title: 'Préparation actualisée',
        description: 'Les contrôles ont changé depuis la dernière préparation.',
        variant: 'destructive',
      });
      void preparation.rerunAfterDecision();
      return;
    }
    toast({ title: 'Erreur', description: frenchErrorMessage(error), variant: 'destructive' });
  });
  const confirmHeld = useConfirmHeld522Assets();
  const confirmBank = useConfirmBankSources();
  const confirmSpam = useConfirmSpamReview();
  const manualSpotPrice = useSetManualSpotPrice();
  const manualClosingPrice = useSetManualClosingPrice();
  const { requestFec, fecDialog, fecDownload } = useFecExport();

  useEffect(() => {
    setHeldDialogOpen(false);
    setBankDialogOpen(false);
    setManualSpotTarget(null);
    setManualClosingTarget(null);
    setClosingPriceFlow(null);
    setSpamRawTxId(null);
  }, [id]);

  if (periods.isLoading) return <Skeleton className="h-48" />;

  const period = periods.data?.find((candidate) => candidate.id === id);
  if (!period) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Période introuvable</CardTitle>
        </CardHeader>
        <CardContent>
          <Link to="/periods" className="text-sm underline">
            Retour à la liste
          </Link>
        </CardContent>
      </Card>
    );
  }

  function selectManualSpotPrice(blocker: CloseBlocker): void {
    if (blocker.assetId === undefined || blocker.timestamp === undefined) return;
    setManualSpotTarget({
      assetId: blocker.assetId,
      symbol: blocker.symbol ?? `Actif #${blocker.assetId}`,
      timestamp: blocker.timestamp,
    });
  }

  function selectManualClosingPrice(blocker: CloseBlocker): void {
    if (blocker.assetId === undefined || blocker.date === undefined) return;
    setManualClosingTarget({
      assetId: blocker.assetId,
      symbol: blocker.symbol ?? `Actif #${blocker.assetId}`,
      date: blocker.date,
    });
  }

  function requestClose(): void {
    close.mutate(id, {
      onSuccess: (result) => {
        showCloseSuccess(result);
        void exports.refetch();
      },
    });
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            {period.label}
            <Badge
              variant={
                period.status === 'LOCKED'
                  ? 'destructive'
                  : period.status === 'CLOSED'
                    ? 'success'
                    : 'secondary'
              }
            >
              {periodStatusLabel(period.status)}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            Du <DateDisplay value={period.startDate} /> au <DateDisplay value={period.endDate} />
          </span>
          {period.status === 'CLOSED' || period.status === 'LOCKED' ? (
            <>
              <Button size="sm" onClick={() => requestFec(id)}>
                Exporter les écritures crypto (format FEC)
              </Button>
              <Button asChild size="sm" variant="outline">
                <a href={`/api/fiscal-periods/${id}/portfolio-statement.xlsx`} download>
                  État du portefeuille — Excel
                </a>
              </Button>
              <Button asChild size="sm" variant="outline">
                <a href={`/api/fiscal-periods/${id}/portfolio-statement.pdf`} download>
                  Rapport de portefeuille — PDF
                </a>
              </Button>
            </>
          ) : null}
          {fecDownload}
        </CardContent>
      </Card>
      {fecDialog}

      {period.status === 'OPEN' ? (
        <ClosePreparationCard
          result={preparation.result}
          running={preparation.running}
          error={preparation.error}
          closing={close.isPending}
          onPrepare={() => void preparation.run()}
          onClose={requestClose}
          onReviewAssets={() => setHeldDialogOpen(true)}
          onReviewBankSources={() => setBankDialogOpen(true)}
          onManualSpotPrice={selectManualSpotPrice}
          onManualClosingPrice={selectManualClosingPrice}
          onReviewSpam={setSpamRawTxId}
        />
      ) : null}

      <Held522DecisionDialog
        open={heldDialogOpen}
        candidates={preparation.result?.assetCandidates ?? []}
        submitting={confirmHeld.isPending}
        onOpenChange={setHeldDialogOpen}
        onSubmit={(input) =>
          confirmHeld.mutate(
            { periodId: id, input },
            {
              onSuccess: () => {
                setHeldDialogOpen(false);
                toast({ title: 'Actifs confirmés en 522' });
                void preparation.rerunAfterDecision();
              },
            },
          )
        }
      />
      <BankSourceDecisionDialog
        open={bankDialogOpen}
        candidates={preparation.result?.bankSourceCandidates ?? []}
        submitting={confirmBank.isPending}
        onOpenChange={setBankDialogOpen}
        onSubmit={(input) =>
          confirmBank.mutate(
            { periodId: id, input },
            {
              onSuccess: () => {
                setBankDialogOpen(false);
                toast({ title: 'Sources bancaires confirmées' });
                void preparation.rerunAfterDecision();
              },
            },
          )
        }
      />
      {manualSpotTarget === null ? null : (
        <ManualSpotPriceDialog
          open
          assetId={manualSpotTarget.assetId}
          symbol={manualSpotTarget.symbol}
          initialTimestamp={manualSpotTarget.timestamp}
          submitting={manualSpotPrice.isPending}
          onOpenChange={(open) => {
            if (!open) setManualSpotTarget(null);
          }}
          onSubmit={(input) =>
            manualSpotPrice.mutate(input, {
              onSuccess: () => {
                setManualSpotTarget(null);
                toast({ title: 'Prix spot enregistré' });
                void preparation.rerunAfterDecision();
              },
            })
          }
        />
      )}
      {manualClosingTarget === null ? null : (
        <ManualClosingPriceDialog
          open
          assetId={manualClosingTarget.assetId}
          symbol={manualClosingTarget.symbol}
          date={manualClosingTarget.date}
          submitting={manualClosingPrice.isPending}
          onOpenChange={(open) => {
            if (!open) setManualClosingTarget(null);
          }}
          onSubmit={(input) =>
            manualClosingPrice.mutate(input, {
              onSuccess: () => {
                setManualClosingTarget(null);
                toast({ title: 'Prix de clôture enregistré' });
                void preparation.rerunAfterDecision();
              },
            })
          }
        />
      )}
      {closingPriceFlow === null ? null : (
        <ManualClosingPriceDialog
          open
          assetId={closingPriceFlow.missing[closingPriceFlow.index]!.assetId}
          symbol={
            assets.data?.find(
              (asset) => asset.id === closingPriceFlow.missing[closingPriceFlow.index]!.assetId,
            )?.symbol ?? `Actif #${closingPriceFlow.missing[closingPriceFlow.index]!.assetId}`
          }
          date={closingPriceFlow.missing[closingPriceFlow.index]!.date}
          submitting={manualClosingPrice.isPending}
          onOpenChange={(open) => {
            if (!open) setClosingPriceFlow(null);
          }}
          onSubmit={(input) =>
            manualClosingPrice.mutate(input, {
              onSuccess: () => {
                const nextIndex = closingPriceFlow.index + 1;
                if (nextIndex < closingPriceFlow.missing.length) {
                  setClosingPriceFlow({ ...closingPriceFlow, index: nextIndex });
                  return;
                }
                setClosingPriceFlow(null);
                toast({ title: 'Prix de clôture enregistrés' });
                requestClose();
              },
            })
          }
        />
      )}
      <SpamReviewDialog
        rawTxId={spamRawTxId}
        submitting={confirmSpam.isPending}
        onOpenChange={(open) => {
          if (!open) setSpamRawTxId(null);
        }}
        onSubmit={async (input) => {
          const result = await confirmSpam.mutateAsync(input);
          setSpamRawTxId(null);
          toast({ title: 'Spam confirmé' });
          await preparation.rerunAfterDecision();
          return result;
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Écritures de la période</CardTitle>
        </CardHeader>
        <CardContent>
          {entries.isLoading ? (
            <Skeleton className="h-32" />
          ) : (
            <JournalView entries={entries.data ?? []} />
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Historique des exports</CardTitle>
        </CardHeader>
        <CardContent>
          {exports.isLoading ? (
            <Skeleton className="h-16" />
          ) : (
            <FecExportHistory exports={exports.data ?? []} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/periods/$periodId')({ component: PeriodDetail });
